import PropTypes from 'prop-types'
import React, {
  useCallback, useEffect, useState,
} from 'react'
import NoSsr from '@material-ui/core/NoSsr'
import Box from '@material-ui/core/Box'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useRouter } from 'next/router'
import { useSendImpressionEvent } from '@lib/gtm-utils'
import {
  getCachedCoachProfileOfferings,
  reviveCoachProfileOfferingsByPersona,
  searchCoachProfileOfferings,
  flattenUniqueCoachProfileOfferings,
} from '@lib/pga-coach/offering-search'
import useFeatureFlags from '@lib/feature-flags/useFeatureFlags'
import { flags } from '@lib/feature-flags/constants'
import { CoachProfileAnalyticsProvider } from '@lib/pga-coach/coach-profile-analytics'
import CoachesProfileLayoutLegacy from '@components/coaches-profile-legacy/CoachesProfileLayoutLegacy'
import { fetchCoachProgramCredentialFlags } from '@lib/pga-coach/coach-program-credential-flags'
import CoachOfferingGroupEvents from '../coach-offering-group-events'
import CoachProfileTestimonials from '../coach-profile-testimonials'
import CoachBio from '../coach-bio/coach-bio'
import {
  showCoachMeetContactTabs,
  isCoachProfileSectionAnchorId,
  takeCoachProfilePendingScroll,
  scrollCoachProfileSectionIntoViewWhenPresent,
  buildCoachProfileSectionHref,
  preserveQueryOnHref,
  tabIdFromSection,
} from '../coach-bio/coach-profile-tab-utils'
import CoachExperiences from '../coach-experiences'
import CoachArticlesQuery from '../coach-articles'
import CoachFacilities from '../coach-facilities'
import useStyles from './coaches-page.styles'

function resolveSeededOfferings(initialOfferingsByPersona) {
  if (initialOfferingsByPersona != null) {
    return reviveCoachProfileOfferingsByPersona(initialOfferingsByPersona)
  }
  return undefined
}

function CoachesPage(props) {
  const [coachProfileUxEnabled] = useFeatureFlags([flags.FLAG_COACH_PROFILE_UX])
  const { coach } = props
  if (!coachProfileUxEnabled) {
    return <CoachesProfileLayoutLegacy coach={coach} />
  }
  return <CoachesPageProfileUx {...props} />
}

function CoachesPageProfileUx(props) {
  const styles = useStyles()
  const [coachAccreditationsEnabled] = useFeatureFlags([
    flags.FLAG_COACH_ACCREDITATIONS,
  ])
  const { coach, section, initialOfferingsByPersona } = props
  const theme = useTheme()
  const router = useRouter()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const coachProfileTabbed = showCoachMeetContactTabs(coach, isDesktop)

  // Section/profileTab is now driven by the URL (see pages/coach/[slug] and
  // pages/coach/[slug]/[section]). We mirror it into local state only so
  // optimistic transitions during `router.push` don't flash the previous tab.
  const tabFromUrl = tabIdFromSection(section)
  const [profileTab, setProfileTabState] = useState(tabFromUrl)

  useEffect(() => {
    setProfileTabState(tabFromUrl)
  }, [tabFromUrl])

  const navigateToTab = useCallback((tabId, hash) => {
    if (!coach?.customSlug) return
    setProfileTabState(tabId)
    const path = buildCoachProfileSectionHref(coach.customSlug, tabId)
    if (!path) return
    const href = preserveQueryOnHref(hash ? `${path}#${hash}` : path, router.asPath)
    if (href !== router.asPath) {
      router.push(href)
    }
  }, [coach?.customSlug, router])

  const coachId = coach?.coach?.id
  const seededOfferings = resolveSeededOfferings(initialOfferingsByPersona)
  const hasSeededOfferings = seededOfferings !== undefined
  const [algoliaResolved, setAlgoliaResolved] = useState(hasSeededOfferings)
  const [minTimeElapsed, setMinTimeElapsed] = useState(hasSeededOfferings)
  const [offeringsByPersona, setOfferingsByPersona] = useState(
    seededOfferings ?? {},
  )
  const offeringsLoading = !algoliaResolved || !minTimeElapsed

  /**
   * `hasPgaHopeProgram` is true when Coach Tools marks the coach as a
   * PGA HOPE coach (`coach.pgaHopeCoach`) and/or when they host
   * upcoming registrable HOPE programs in Algolia (see
   * `coach-program-credential-flags.js`). Feeds the PGA HOPE card on
   * `<CoachAccreditationsRow>`. SSR seeds from `pgaHopeCoach`; the
   * Algolia fetch can additionally flip it on.
   */
  const pgaHopeCoach = coach?.coach?.pgaHopeCoach === true
  const [hasPgaHopeProgram, setHasPgaHopeProgram] = useState(pgaHopeCoach)
  useEffect(() => {
    if (!coachAccreditationsEnabled) {
      setHasPgaHopeProgram(false)
      return undefined
    }
    // Reset synchronously on coach change so client navigation does not
    // leak the previous coach's HOPE state before Algolia resolves.
    setHasPgaHopeProgram(pgaHopeCoach)
    if (!coachId) {
      return undefined
    }
    let cancelled = false
    fetchCoachProgramCredentialFlags(coachId)
      .then((credentialFlags) => {
        if (!cancelled) {
          setHasPgaHopeProgram(
            pgaHopeCoach || Boolean(credentialFlags?.hasPgaHopeProgram),
          )
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [coach?.coach?.id, coachAccreditationsEnabled, pgaHopeCoach])

  useEffect(() => {
    if (!coachId) {
      setAlgoliaResolved(true)
      setMinTimeElapsed(true)
      return undefined
    }
    const seeded = resolveSeededOfferings(initialOfferingsByPersona)
    if (seeded !== undefined) {
      setOfferingsByPersona(seeded)
      setAlgoliaResolved(true)
      setMinTimeElapsed(true)
      return undefined
    }
    const cached = getCachedCoachProfileOfferings(coachId)
    if (cached !== undefined) {
      setOfferingsByPersona(reviveCoachProfileOfferingsByPersona(cached))
      setAlgoliaResolved(true)
      setMinTimeElapsed(true)
      return undefined
    }
    let cancelled = false
    setAlgoliaResolved(false)
    setMinTimeElapsed(false)

    const minTimer = setTimeout(() => {
      if (!cancelled) setMinTimeElapsed(true)
    }, 1000)

    searchCoachProfileOfferings(coachId)
      .then(({ offeringsByPersona: result }) => {
        if (!cancelled) setOfferingsByPersona(result)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setAlgoliaResolved(true)
      })

    return () => {
      cancelled = true
      clearTimeout(minTimer)
    }
  }, [coachId, initialOfferingsByPersona])

  const scrollToCoachProfileSection = useCallback((anchorId) => {
    if (!anchorId) return
    if (typeof window !== 'undefined') {
      scrollCoachProfileSectionIntoViewWhenPresent(anchorId)
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const fromSession = takeCoachProfilePendingScroll()
    const fromHash = window.location.hash.replace(/^#/, '')
    const hashAnchor = isCoachProfileSectionAnchorId(fromHash) ? fromHash : null
    const anchorId = fromSession || hashAnchor
    if (!anchorId) return undefined
    scrollCoachProfileSectionIntoViewWhenPresent(anchorId)
    // Native `#hash` (and Next after `/contact` 302) can land under the
    // sticky header; retry once after that jump.
    const retry = window.setTimeout(() => {
      scrollCoachProfileSectionIntoViewWhenPresent(anchorId)
    }, 200)
    return () => window.clearTimeout(retry)
  }, [profileTab])

  useEffect(() => {
    const { introductionVideoUrl } = coach
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'coach-profile-view',
      info: [{ 'has-video': !!introductionVideoUrl }],
    })
  }, [coach])

  const findExperiences = (experienceTypes, typeName) => {
    const experience = experienceTypes.find((exp) => exp.typeName === typeName)
    if (experience) {
      return experience.experiences.map((opt) => opt.experienceName)
    }
    return []
  }

  const findServices = (serviceTypes, typeName) => {
    const service = serviceTypes.find((serv) => serv.typeName === typeName)
    if (service) {
      return service.services.map((opt) => opt.serviceName)
    }
    return []
  }

  useSendImpressionEvent({
    event: 'view-coach-profile',
    attributes: {
      coach_name:
        coach?.firstName && coach?.lastName
          ? `${coach?.firstName} ${coach?.lastName}`
          : `${coach?.displayName}`,
      has_video: !!coach?.introductionVideoUrl,
      has_profile_photo: !!coach?.profilePhoto,
      city: coach?.city,
      state: coach?.state,
      facility:
        coach?.facilities.length > 0 ? coach?.facilities[0].facilityName : null,
      bookable: coach?.bookable,
      coach_id: coach?.coach?.id,
      gender: coach?.gender,
      player_type: findServices(coach?.serviceTypes, 'Player Type'),
      session_type: findServices(coach?.serviceTypes, 'Session Type'),
      getting_started: findExperiences(
        coach?.experienceTypes,
        'Getting Started',
      ),
      having_more_fun: findExperiences(
        coach?.experienceTypes,
        'Having More Fun',
      ),
      improving_your_game: findExperiences(
        coach?.experienceTypes,
        'Improving Your Game',
      ),
      students: coach?.numberOfStudents,
      followers: coach?.numberOfFollowers,
      socials:
        coach?.socialJsonData?.length > 0
          ? coach?.socialJsonData.map((social) => social.type)
          : null,
      has_photos: coach?.photosJsonData?.length > 0,
      coaching_available_to_public: coach?.facilities.some(
        (fac) => fac.isPrivate === false,
      ),
      has_quotes: coach?.testimonialsJsonData?.length > 0,
      has_plus_subscription: coach?.coach?.hasPaidTierSubscription,
      member_type: coach?.coach?.memberType,
      certification_level: coach?.certificationLevel,
      accepts_leads: coach?.coach?.preferences?.acceptsLeads,
      actionable: coach?.coach?.preferences?.acceptsLeads || (coach?.bookable && !!coach?.bookingURL),
    },
  })

  return (
    <CoachProfileAnalyticsProvider coach={coach}>
      <div
        className={styles.root}
        data-testid="coach-profile-page"
      >
        {/* Two-column marketplace: sticky booking card left, content right from md. */}
        <CoachBio
          coach={coach}
          profileTab={coachProfileTabbed ? profileTab : undefined}
          onProfileTabChange={coachProfileTabbed ? navigateToTab : undefined}
          onScrollToSection={scrollToCoachProfileSection}
          offeringsLoading={offeringsLoading}
          offeringsByPersona={offeringsByPersona}
          coachAccreditationsEnabled={coachAccreditationsEnabled}
          hasPgaHopeProgram={hasPgaHopeProgram}
        >
          <NoSsr>
            <CoachOfferingGroupEvents
              coach={coach}
              photosJsonData={coach.photosJsonData}
            />
          </NoSsr>

          {/*
          Experiences — visually hidden, but retained in the DOM so search
          engines continue to index the coach's experience keywords, persona
          labels, lesson types, and skills for SEO.
        */}
          <Box
            aria-hidden="true"
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0,
            }}
          >
            <CoachExperiences
              experiences={coach.experienceTypes}
              firstName={coach.firstName}
              serviceTypes={coach.serviceTypes}
              skills={[]}
            />
          </Box>
          <CoachArticlesQuery coach={coach} />
          <CoachProfileTestimonials testimonialsData={coach.testimonialsJsonData} />
          {coach.facilities?.length > 1 ? (
            <CoachFacilities facilities={coach.facilities} coach={coach} />
          ) : null}
        </CoachBio>
      </div>
    </CoachProfileAnalyticsProvider>
  )
}

const gendersList = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'O', label: 'Other' },
]

CoachesPageProfileUx.propTypes = {
  /** Active section slug from the URL (`about`, `contact`, persona id). */
  section: PropTypes.string,
  /** SSR-prefetched offering map for the About shop. */
  initialOfferingsByPersona: PropTypes.objectOf(PropTypes.array),
  coach: PropTypes.shape({
    bookable: PropTypes.bool,
    bookingURL: PropTypes.string,
    coach: PropTypes.shape({
      id: PropTypes.string,
      preferences: PropTypes.shape({
        acceptsLeads: PropTypes.bool,
      }),
      memberType: PropTypes.string,
      certificationLevel: PropTypes.string,
      hasPaidTierSubscription: PropTypes.bool,
    }),
    coachProfileId: PropTypes.string,
    certificationLevel: PropTypes.string,
    memberType: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    displayName: PropTypes.string,
    title: PropTypes.string,
    gender: PropTypes.oneOf([
      ...gendersList,
      ...gendersList.map((g) => g.value),
    ]),
    profilePhoto: PropTypes.string,
    experienceTypes: PropTypes.array,
    serviceTypes: PropTypes.array,
    customSlug: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zipCode: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    bio: PropTypes.string,
    numberOfStudents: PropTypes.number,
    numberOfFollowers: PropTypes.number,
    socialJsonData: PropTypes.array,
    testimonialsJsonData: PropTypes.array,
    photosJsonData: PropTypes.array,
    toolJsonData: PropTypes.array,
    status: PropTypes.string,
    facilities: PropTypes.array,
    experience: PropTypes.array,
    service: PropTypes.array,
    skills: PropTypes.array,
    introductionVideoUrl: PropTypes.string,
    hasPaidTierSubscription: PropTypes.bool,
  }),
}

CoachesPageProfileUx.defaultProps = {
  coach: {
    coachProfileId: '',
  },
}

CoachesPage.propTypes = CoachesPageProfileUx.propTypes
CoachesPage.defaultProps = CoachesPageProfileUx.defaultProps

export default CoachesPage
